import React, { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { updateResidenceData } from '../../../actions/refiApplicationActions'
import { selectResidenceData } from '../../../reducers/refiApplication'
import { updateAddress } from '../../../features/users/userActions'
import { displayNotification } from '../../../actions/notificationsActions'

interface ResidenceDetailsFormProps {
  vehicle?: any
  user?: any
}

const ResidenceDetailsForm: React.FC<ResidenceDetailsFormProps> = ({ user }) => {
  const history = useHistory()
  const dispatch = useDispatch()
  const savedResidenceData = useSelector(selectResidenceData)

  const [formData, setFormData] = useState({
    monthlyPayment: savedResidenceData.monthlyPayment || '',
    moveInMonth: savedResidenceData.moveInMonth || '',
    moveInDay: savedResidenceData.moveInDay || '',
    moveInYear: savedResidenceData.moveInYear || '',
    ownershipStatus: savedResidenceData.ownershipStatus || 'rent'
  })
  const [errors, setErrors] = useState<{[key: string]: string}>({})
  const [touched, setTouched] = useState<{[key: string]: boolean}>({})

  useEffect(() => {
    if (savedResidenceData.monthlyPayment || savedResidenceData.moveInMonth) {
      setFormData({
        monthlyPayment: savedResidenceData.monthlyPayment || '',
        moveInMonth: savedResidenceData.moveInMonth || '',
        moveInDay: savedResidenceData.moveInDay || '',
        moveInYear: savedResidenceData.moveInYear || '',
        ownershipStatus: savedResidenceData.ownershipStatus || 'rent'
      })
    }
  }, [])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const handleOwnershipStatusChange = (status: string) => {
    setFormData(prev => ({
      ...prev,
      ownershipStatus: status
    }))
  }

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {}

    if (!formData.monthlyPayment.trim()) {
      newErrors.monthlyPayment = 'Monthly payment is required'
    } else if (!/^\$?[\d,]+\.?\d*$/.test(formData.monthlyPayment.trim())) {
      newErrors.monthlyPayment = 'Please enter a valid amount (e.g., $123.00)'
    }

    if (!formData.moveInMonth) {
      newErrors.moveInMonth = 'Month is required'
    }

    if (!formData.moveInDay) {
      newErrors.moveInDay = 'Day is required'
    }

    if (!formData.moveInYear) {
      newErrors.moveInYear = 'Year is required'
    }

    // Validate date is not in the future
    if (formData.moveInMonth && formData.moveInDay && formData.moveInYear) {
      const moveInDate = new Date(parseInt(formData.moveInYear, 10), parseInt(formData.moveInMonth, 10) - 1, parseInt(formData.moveInDay, 10))
      const today = new Date()
      today.setHours(0, 0, 0, 0)

      if (moveInDate > today) {
        newErrors.moveInDate = 'Move-in date cannot be in the future'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleNext = () => {
    setTouched({
      monthlyPayment: true,
      moveInMonth: true,
      moveInDay: true,
      moveInYear: true
    })

    if (validateForm()) {
      dispatch(updateResidenceData(formData))

      const address = user?.addresses?.[0]
      if (!address) {
        dispatch(displayNotification('No address found', 'error'))
        return
      } else {
        const ownershipMapping = {
          RENT: 'Rent',
          OWN: 'Own',
          FAMILY: 'Live with Others',
          OTHER: 'Other',
        }
        const mappedOwnership =
          ownershipMapping[formData.ownershipStatus] || formData.ownershipStatus

        dispatch(
          updateAddress({
            id: address.id,
            ownership: mappedOwnership,
          })
        )
      }

      history.push('/refiApp/financing/apply/income-details')
    }
  }

  const handleBack = () => {
    history.push('/refiApp/financing/apply/applicant-info')
  }

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ]

  const days = Array.from({ length: 31 }, (_, i) => i + 1)
  const currentYear = new Date().getFullYear()
  const years = Array.from({ length: 10 }, (_, i) => currentYear - i)

  return (
    <div className="residence-details-form" style={{ maxWidth: '540px', margin: '0 auto', padding: '20px' }}>
      <div className="housing-form">
        <div className="loan-form__address" style={{display: 'flex', alignItems: 'center', marginBottom: '10px', gap: '10px'}}>
          <h1> We found a location</h1>
          <p style={{marginBottom: '1.8rem'}}> {user?.addresses[0]?.line1} {user?.addresses[0]?.line2} {user?.addresses[0]?.city}, {user?.addresses[0]?.state} </p>
        </div>
        <div className="housing-form__header">
          <p className="housing-form__description">
            {user?.firstName || 'Hi'}, our partners use your housing info to find the best matched lenders in your area.
          </p>
        </div>

        <div className="housing-form__fields">
          <div className="form__section">
            <label htmlFor="monthlyPayment" className="form__label">
              Monthly Payment
            </label>
            <input
              id="monthlyPayment"
              name="monthlyPayment"
              type="text"
              value={formData.monthlyPayment}
              onChange={handleInputChange}
              onBlur={() => setTouched(prev => ({ ...prev, monthlyPayment: true }))}
              placeholder="example: $123.00"
              className={`form__input form__input--full-width ${errors.monthlyPayment ? 'form__input--error' : ''}`}
            />
            {touched.monthlyPayment && errors.monthlyPayment && (
              <p className="form__help-text text-error" style={{ marginTop: '2px', marginBottom: '0' }}>{errors.monthlyPayment}</p>
            )}
          </div>

          <div className="form__section">
            <label className="form__label">Move In Date</label>
            <div style={{ display: 'flex', gap: '10px' }}>
              <div style={{ flex: 1 }}>
                <label htmlFor="moveInMonth" className="form__label" style={{ fontSize: '14px', marginBottom: '5px' }}>
                  Month
                </label>
                <select
                  id="moveInMonth"
                  name="moveInMonth"
                  value={formData.moveInMonth}
                  onChange={handleSelectChange}
                  onBlur={() => setTouched(prev => ({ ...prev, moveInMonth: true }))}
                  className={`form__input ${errors.moveInMonth ? 'form__input--error' : ''}`}
                  style={{ width: '100%' }}
                >
                  <option value="">MM</option>
                  {months.map((month, index) => (
                    <option key={month} value={index + 1}>
                      {month}
                    </option>
                  ))}
                </select>
                {touched.moveInMonth && errors.moveInMonth && (
                  <p className="form__help-text text-error" style={{ fontSize: '12px', marginTop: '2px', marginBottom: '0' }}>{errors.moveInMonth}</p>
                )}
              </div>
              <div style={{ flex: 1 }}>
                <label htmlFor="moveInDay" className="form__label" style={{ fontSize: '14px', marginBottom: '5px' }}>
                  Day
                </label>
                <select
                  id="moveInDay"
                  name="moveInDay"
                  value={formData.moveInDay}
                  onChange={handleSelectChange}
                  onBlur={() => setTouched(prev => ({ ...prev, moveInDay: true }))}
                  className={`form__input ${errors.moveInDay ? 'form__input--error' : ''}`}
                  style={{ width: '100%' }}
                >
                  <option value="">DD</option>
                  {days.map((day) => (
                    <option key={day} value={day}>
                      {day}
                    </option>
                  ))}
                </select>
                {touched.moveInDay && errors.moveInDay && (
                  <p className="form__help-text text-error" style={{ fontSize: '12px', marginTop: '2px', marginBottom: '0' }}>{errors.moveInDay}</p>
                )}
              </div>
              <div style={{ flex: 1 }}>
                <label htmlFor="moveInYear" className="form__label" style={{ fontSize: '14px', marginBottom: '5px' }}>
                  Year
                </label>
                <select
                  id="moveInYear"
                  name="moveInYear"
                  value={formData.moveInYear}
                  onChange={handleSelectChange}
                  onBlur={() => setTouched(prev => ({ ...prev, moveInYear: true }))}
                  className={`form__input ${errors.moveInYear ? 'form__input--error' : ''}`}
                  style={{ width: '100%' }}
                >
                  <option value="">YYYY</option>
                  {years.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
                {touched.moveInYear && errors.moveInYear && (
                  <p className="form__help-text text-error" style={{ fontSize: '12px', marginTop: '2px', marginBottom: '0' }}>{errors.moveInYear}</p>
                )}
              </div>
            </div>
            {touched.moveInMonth && errors.moveInDate && (
              <p className="form__help-text text-error" style={{ marginTop: '2px', marginBottom: '0' }}>{errors.moveInDate}</p>
            )}
          </div>

          <div className="form__section">
            <label className="form__label">Ownership Status</label>
            <div style={{ display: 'flex', gap: '5px' }}>
              <button
                type="button"
                onClick={() => handleOwnershipStatusChange('RENT')}
                className={`button ${formData.ownershipStatus === 'RENT' ? 'button--primary' : 'button--secondary'}`}
                style={{
                  flex: 1,
                  borderRadius: '4px',
                  backgroundColor: formData.ownershipStatus === 'RENT' ? '#007bff' : '#fff',
                  color: formData.ownershipStatus === 'RENT' ? '#fff' : '#333',
                  border: '1px solid #ddd'
                }}
              >
                Rent
              </button>
              <button
                type="button"
                onClick={() => handleOwnershipStatusChange('OWN')}
                className={`button ${formData.ownershipStatus === 'OWN' ? 'button--primary' : 'button--secondary'}`}
                style={{
                  flex: 1,
                  borderRadius: '4px',
                  backgroundColor: formData.ownershipStatus === 'OWN' ? '#007bff' : '#fff',
                  color: formData.ownershipStatus === 'OWN' ? '#fff' : '#333',
                  border: '1px solid #ddd'
                }}
              >
                Own
              </button>
              <button
                type="button"
                onClick={() => handleOwnershipStatusChange('FAMILY')}
                className={`button ${formData.ownershipStatus === 'FAMILY' ? 'button--primary' : 'button--secondary'}`}
                style={{
                  flex: 1,
                  borderRadius: '4px',
                  backgroundColor: formData.ownershipStatus === 'FAMILY' ? '#007bff' : '#fff',
                  color: formData.ownershipStatus === 'FAMILY' ? '#fff' : '#333',
                  border: '1px solid #ddd'
                }}
              >
                Family
              </button>
              <button
                type="button"
                onClick={() => handleOwnershipStatusChange('OTHER')}
                className={`button ${formData.ownershipStatus === 'OTHER' ? 'button--primary' : 'button--secondary'}`}
                style={{
                  flex: 1,
                  borderRadius: '4px',
                  backgroundColor: formData.ownershipStatus === 'OTHER' ? '#007bff' : '#fff',
                  color: formData.ownershipStatus === 'OTHER' ? '#fff' : '#333',
                  border: '1px solid #ddd'
                }}
              >
                Other
              </button>
            </div>
          </div>
        </div>

        <div className="housing-form__actions" style={{ marginTop: '30px' }}>
          <button
            type="button"
            onClick={handleNext}
            className="button button--primary button--full-width"
          >
            Next
          </button>
        </div>

        <div className="housing-form__navigation" style={{ marginTop: '20px' }}>
          <button
            type="button"
            onClick={handleBack}
            className="button button--link-text"
          >
            Back
          </button>
        </div>
      </div>
    </div>
  )
}

ResidenceDetailsForm.displayName = 'Residence Details Form'

export default ResidenceDetailsForm
